/**
 * 伴学 IM 750 分片按 sort 去乱序、连续前缀再拼接；与 bloom-H5 共用逻辑。
 * 无 sort 或非法 sort 时由调用方走原有「直接拼在 agentRes 后」的兼容模式。
 * 展示串在连续前缀之后追加「尾部孤儿 sort」（升序），避免缓冲清空后仅迟到大序号分片时丢字。
 */

/** IM 750 分片长期不齐时超时强制结束等待（毫秒）；收到结束标识后会先行解除等待，此为兜底 */
export const IM750_WAIT_FORCE_MS = 10000

/**
 * @param {object} msg
 * @param {object|null} md 已 parse 的 messageData
 * @returns {number|null}
 */
export function extractSortFromMsg (msg, md) {
  const pick = (o) => {
    if (!o || typeof o !== 'object') return null
    const s = o.sort != null ? Number(o.sort) : NaN
    if (Number.isFinite(s) && s >= 1) return Math.floor(s)
    return null
  }
  const a = pick(msg)
  if (a != null) return a
  return pick(md)
}

/**
 * totalParts / total / maxSort：任一包带上即可，用于在分片到齐时 streamDone；UI 上「是否仍等待」在 answerComplete 时与 streamDone 解耦（见 mergeIm750AgentResState）。
 * @param {object} msg
 * @param {object|null} md
 * @returns {number|null}
 */
export function extractTotalPartsFromMsg (msg, md) {
  const pick = (o) => {
    if (!o || typeof o !== 'object') return null
    const t =
      o.totalParts != null ? o.totalParts : o.total != null ? o.total : o.maxSort
    const n = t != null ? Number(t) : NaN
    if (Number.isFinite(n) && n >= 1) return Math.floor(n)
    return null
  }
  const a = pick(msg)
  if (a != null) return a
  return pick(md)
}

export function shouldUseSortOnPacket (sort) {
  return sort != null && Number.isFinite(sort) && sort >= 1
}

export function getIm750SortMeta (msg) {
  if (!msg || typeof msg !== 'object') {
    return { sort: null, totalParts: null, md: null }
  }
  let md = msg.messageData
  if (md != null && typeof md === 'string') {
    const t = md.trim()
    if (t.startsWith('{') || t.startsWith('[')) {
      try {
        md = JSON.parse(md)
      } catch (e) {
        md = null
      }
    } else {
      md = null
    }
  }
  return {
    sort: extractSortFromMsg(msg, md),
    totalParts: extractTotalPartsFromMsg(msg, md),
    md
  }
}

/** 按 aiDialogueChatRecordId 存 750 分片状态，不挂 Vue data，避免热更新/未初始化时 undefined[rid] */
const im750RecordBuffers = Object.create(null)

export function getIm750RecordBuffer (rid) {
  if (rid == null || rid === '') return null
  const k = String(rid)
  return im750RecordBuffers[k] != null ? im750RecordBuffers[k] : null
}

export function setIm750RecordBuffer (rid, state) {
  if (rid == null || rid === '') return
  im750RecordBuffers[String(rid)] = state
}

export function removeIm750RecordBuffer (rid) {
  if (rid == null || rid === '') return
  delete im750RecordBuffers[String(rid)]
}

export function clearIm750RecordBuffers () {
  for (const k in im750RecordBuffers) {
    if (Object.prototype.hasOwnProperty.call(im750RecordBuffers, k)) {
      delete im750RecordBuffers[k]
    }
  }
}

function chunkAt (chunks, i) {
  if (chunks == null) return undefined
  const a = chunks[i]
  if (a != null) return a
  return chunks[String(i)]
}

/**
 * 将 sort=1,2,… 且中间无缺失 的片段顺序拼接为展示用 agentRes。
 * @param {Record<number|string, string>} chunks
 */
export function joinContiguousChunks (chunks) {
  let k = 1
  let out = ''
  let v = chunkAt(chunks, k)
  while (v != null && v !== undefined) {
    out += String(v)
    k++
    v = chunkAt(chunks, k)
  }
  return out
}

/**
 * 连续前缀（从 1 起无洞）+ 其后的分片按 sort 升序追加。
 * 解决：streamDone 已清缓冲后迟到包仅带大 sort、新 chunks 无 1…k-1 时，仅 joinContiguousChunks 会得到空串导致丢字（如 topicNameListStr 围栏）。
 * 说明：若中间某档仍迟到，可能短暂出现「先展示更后序号」的乱序，到齐后下一轮合并会整段重算为严序（与仅乱序、无缺档的协议一致）。
 * @param {Record<number|string, string>} chunks
 */
export function joinChunksForAgentResDisplay (chunks) {
  if (chunks == null || typeof chunks !== 'object') return ''
  const head = joinContiguousChunks(chunks)
  let m = 0
  for (let i = 1; ; i++) {
    const v = chunkAt(chunks, i)
    if (v == null || v === undefined) break
    m = i
  }
  const extraKeys = Object.keys(chunks)
    .map((k) => Number(k))
    .filter((n) => Number.isFinite(n) && n >= 1 && n > m)
    .sort((a, b) => a - b)
  if (extraKeys.length === 0) return head
  let tail = ''
  for (const n of extraKeys) {
    const v = chunkAt(chunks, n)
    if (v != null && v !== undefined) tail += String(v)
  }
  return head + tail
}

function maxChunkKey (chunks) {
  const keys = Object.keys(chunks)
    .map((k) => Number(k))
    .filter((n) => Number.isFinite(n) && n >= 1)
  return keys.length ? Math.max.apply(null, keys) : 0
}

function contiguousRangeComplete (chunks, expectedMax) {
  for (let i = 1; i <= expectedMax; i++) {
    const v = chunkAt(chunks, i)
    if (v == null || v === undefined) return false
  }
  return true
}

function hasNoGapsUpToMax (chunks) {
  const m = maxChunkKey(chunks)
  if (m === 0) return true
  return contiguousRangeComplete(chunks, m)
}

/**
 * answerComplete 为真且分片已齐时 streamDone 为真；分片未齐时 streamDone 仍可能为假，但 nextWait 在 answerComplete 时恒为假（不锁发送区）。
 * 约定：结束包 sort=S_max 可无正文，但须在 chunks[S_max] 占位（见 mergeIm750AgentResState）；
 * 有 totalPartsHint 时须 1…S_max 全部已到达（含空串），再结束等待。
 * @param {Record<number|string, string>} chunks
 * @param {number|null|undefined} totalPartsHint
 * @param {boolean} answerComplete
 */
export function isIm750SortStreamFinished (chunks, totalPartsHint, answerComplete) {
  if (!answerComplete) return false
  if (!chunks || typeof chunks !== 'object') return false
  if (totalPartsHint != null && totalPartsHint >= 1) {
    return contiguousRangeComplete(chunks, totalPartsHint)
  }
  return hasNoGapsUpToMax(chunks) && maxChunkKey(chunks) >= 1
}

/**
 * 合并 750 包：有 sort 走缓冲，否则回退为 legacy 追加。
 * @returns {{ agentRes: string, nextWait: boolean, streamDone: boolean, useBuffer: boolean }}
 */
export function mergeIm750AgentResState ({
  prevAgentRes,
  contentPiece,
  msgAgentRes,
  isErrorKind,
  answerComplete,
  sort,
  totalParts,
  recordBuffer
}) {
  const prev = prevAgentRes != null ? String(prevAgentRes) : ''
  if (isErrorKind) {
    return {
      agentRes: prev,
      nextWait: !answerComplete,
      streamDone: answerComplete,
      useBuffer: false
    }
  }

  let rb = recordBuffer
  if (rb != null && (typeof rb !== 'object' || Array.isArray(rb))) {
    rb = null
  }

  const useBuffer =
    rb != null ||
    shouldUseSortOnPacket(sort)

  if (!useBuffer) {
    const next = contentPiece
      ? prev + String(contentPiece)
      : (msgAgentRes ? String(msgAgentRes) : prev)
    return {
      agentRes: next,
      nextWait: !answerComplete,
      streamDone: answerComplete,
      useBuffer: false
    }
  }

  const state = rb || { chunks: Object.create(null), totalPartsHint: null, legacyPrefix: null }
  if (!state.chunks || typeof state.chunks !== 'object') {
    state.chunks = Object.create(null)
  }
  if (totalParts != null) {
    state.totalPartsHint = totalParts
  }
  if (
    !rb &&
    String(prev).trim() !== '' &&
    shouldUseSortOnPacket(sort)
  ) {
    state.legacyPrefix = String(prev)
  }

  if (shouldUseSortOnPacket(sort)) {
    const fromContent = contentPiece != null && String(contentPiece) !== '' ? String(contentPiece) : null
    const fromAgent = msgAgentRes != null && String(msgAgentRes) !== '' ? String(msgAgentRes) : null
    const piece = fromContent != null ? fromContent : fromAgent
    if (piece != null) {
      state.chunks[sort] = piece
    } else if (answerComplete) {
      const existing = chunkAt(state.chunks, sort)
      if (existing === undefined || existing === null) {
        state.chunks[sort] = ''
      }
    }
    if (answerComplete) {
      const prevHint =
        state.totalPartsHint != null && state.totalPartsHint >= 1 ? state.totalPartsHint : 0
      state.totalPartsHint = Math.max(prevHint, sort)
    }
  }

  const prefix =
    state.legacyPrefix != null && String(state.legacyPrefix) !== ''
      ? String(state.legacyPrefix)
      : ''
  const display = prefix + joinChunksForAgentResDisplay(state.chunks)
  const totalHint = state.totalPartsHint
  const streamDone = isIm750SortStreamFinished(state.chunks, totalHint, answerComplete)
  /** 服务端已标记本轮结束时立即放开发送/等待；迟到分片仍会继续写入 agentRes，不因 sort 暂不齐而锁 UI */
  const nextWait = answerComplete ? false : !streamDone

  return {
    agentRes: display,
    nextWait,
    streamDone,
    useBuffer: true,
    nextBuffer: state
  }
}



// WEBPACK FOOTER //
// ./src/utils/im750SortBuffer.js